import React from 'react';
import { Box } from '@mui/material';
import { EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SurfaceCard } from '../ui/SurfaceCard';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { MEDALS } from './LeaderboardTable';
import { formatKm } from './contestFormat';
import { StandingEntry } from './useContestStandings';

interface ContestPodiumProps {
  standings: StandingEntry[];
  isTeam: boolean;
}

// Final top three for an ended contest; entries with no distance never place.
export const ContestPodium = ({ standings, isTeam }: ContestPodiumProps) => {
  const { t } = useTranslation('contests');
  const podium = standings.filter(s => s.totalDistance > 0).slice(0, 3);
  if (podium.length === 0) return null;

  return (
    <SurfaceCard title={isTeam ? t('podium.winningTeams') : t('podium.winners')} subtitle={t('podium.subtitle')}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {podium.map(entry => {
          const medal = MEDALS[entry.rank];
          return (
            <Box
              key={entry.id}
              sx={{ display: 'flex', gap: 1.25, alignItems: 'center', p: '12px 14px', borderRadius: '10px', border: '1px solid var(--border)', minWidth: 0 }}
            >
              <Box
                sx={{ flex: 'none', width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: medal?.bg, color: medal?.fg, '& svg': { fontSize: 16 } }}
                aria-label={t('podium.rankLabel', { rank: entry.rank })}
              >
                {entry.rank === 1 ? <TrophyIcon /> : <Box component="span" sx={{ fontSize: 13, fontWeight: 600 }}>{entry.rank}</Box>}
              </Box>
              <InitialsAvatar name={entry.name} size={30} shape={isTeam ? 'rounded' : undefined} />
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Box component="span" sx={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.name}</Box>
                <Box component="span" sx={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{formatKm(entry.totalDistance)}</Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </SurfaceCard>
  );
};
